import React, { useState, useRef, useEffect } from "react";
import { getSidebarUserProfileClasses } from "./constants";
import noProfileImg from "@surgia/ui/icons/sidebar/noprofileImg.svg";
import chevronIcon from "@surgia/ui/icons/sidebar/chevronUpDown.svg";
import { useTranslation } from "react-i18next";

export interface UserProfileProps {
  isCollapsed?: boolean;
  className?: string;
  onProfileClick?: (userProfile: any) => void;
  userProfile: any;
  onSignOut: () => void;
}

const UserProfileAvatar: React.FC<{
  isCollapsed: boolean;
}> = ({ isCollapsed }) => {
  const profileClasses = getSidebarUserProfileClasses(isCollapsed);
  return (
    <div className="relative">
      <img
        src={noProfileImg}
        // alt={userProfile?.name}
        className={profileClasses.avatar}
      />

      <span
        className={`${profileClasses.statusIndicator.base} ${
          profileClasses.statusIndicator["online"]
        }`}
      />
    </div>
  );
};

const UserProfileInfo: React.FC<{
  userProfile: { name: string; email: string };
  isCollapsed: boolean;
}> = ({ userProfile, isCollapsed }) => {
  const profileClasses = getSidebarUserProfileClasses(isCollapsed);
  if (isCollapsed) return null;

  return (
    <div className={profileClasses.info}>
      <span className={profileClasses.name}>{userProfile?.name}</span>
      <span className="text-xs text-gray-500 max-w-[150px] truncate">
        {userProfile?.email}
      </span>
    </div>
  );
};

const UserProfileChevron: React.FC<{
  isCollapsed: boolean;
}> = ({ isCollapsed }) => {
  const profileClasses = getSidebarUserProfileClasses(isCollapsed);

  if (isCollapsed) return null;

  return (
    <div className={profileClasses.chevron}>
      <img src={chevronIcon} alt="Toggle" />
    </div>
  );
};

const UserProfile: React.FC<UserProfileProps> = ({
  isCollapsed = false,
  className = "",
  userProfile,
  onSignOut,
}) => {
  const [open, setOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  // Close popup when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const profileClasses = getSidebarUserProfileClasses(isCollapsed);

  // When collapsed, only show the avatar with tooltip
  if (isCollapsed) {
    return (
      <div className={`mt-auto pb-4 ${className}`}>
        <div className="relative group cursor-pointer">
          <img
            src={noProfileImg}
            // alt={userProfile?.name}
            className="w-10 h-10 rounded-full overflow-hidden"
          />
          <span
            className={`w-2.5 h-2.5 rounded-full absolute bottom-1 right-1 border-2 border-white ${"bg-green-500"}`}
          />

          {/* Tooltip for collapsed state */}
          <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
            {/* <div className="font-semibold">{userProfile?.name}</div>
            <div className="text-xs opacity-75">{userProfile?.email}</div> */}
          </div>
        </div>
      </div>
    );
  }

  // When expanded, show full profile with container
  return (
    <div
      className={`${profileClasses.container} ${className} relative`}
      ref={profileRef}
      dir="ltr"
    >
      <div
        className={`${profileClasses.inner} group cursor-pointer`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        {/* Avatar - Always visible */}
        <UserProfileAvatar isCollapsed={isCollapsed} />

        {/* User Info - Only visible when expanded */}
        <UserProfileInfo isCollapsed={isCollapsed} userProfile={userProfile} />

        {/* Chevron icon - Only visible when expanded */}
        <UserProfileChevron isCollapsed={isCollapsed} />
        {open && (
          <div
            className={`absolute ${isRTL ? "left-[-280px]" : "right-[-280px]"} mb-2 w-64 bg-white rounded-xl shadow-xl z-50 p-3 flex flex-col -top-8`}
          >
            <div className="flex items-center gap-3">
              {/* <img
                src={noProfileImg}
                // alt={userProfile?.name}
                className="w-10 h-10 rounded-full overflow-hidden"
              /> */}
              <div className="ml-2">
                <div className=" text-md -mb-1">{userProfile?.name}</div>
                <div className="text-sm text-gray-500">
                  {userProfile?.email}
                </div>
              </div>
            </div>
            <button
              className="flex items-center rounded  text-left text-sm ml-2 cursor-pointer"
              onClick={() => {
                setOpen(false);
                onSignOut?.();
              }}
            >
              {t("signOut")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
